import type {
	IQuizQuestion,
	IMarkedQuizAnswer,
	IMarkedQuizAnswers
} from "@buildwitt/shapeshift-types";
import {
	isBooleanChoiceQuestion,
	isDragToOrderQuestion,
	isFillTheBlankQuestion,
	isMatchingOrderQuestion,
	isMultipleChoiceQuestion,
	isSingleChoiceQuestion,
	isTextQuestion
} from "@buildwitt/shapeshift-types/IQuiz";
import type {SupportedLocale} from "@/app2/shared/contexts";
import {mapCanonicalToDisplayValue, type InlineQuizApiQuestion} from "./inlineQuizQuestions";

interface ApiMarkedAnswer {
	questionId: number;
	userAnswer?: string | number | string[] | number[] | boolean | (number | boolean)[] | null;
	correctAnswer?: string | number | string[] | number[] | boolean | (number | boolean)[] | null;
	correct?: boolean;
	isCorrect?: boolean;
}

/**
 * Converts API marked answers + questions to IMarkedQuizAnswer format for display.
 * Merges question metadata (title, options) with API result (userAnswer, correctAnswer, correct).
 */
export function convertToMarkedAnswers(
	questions: IQuizQuestion[],
	apiMarkedAnswers: ApiMarkedAnswer[],
	apiResult: { correct: number; incorrect: number; requiredScore: number; pass: boolean },
	resolution?: {apiQuestions: InlineQuizApiQuestion[]; locale: SupportedLocale}
): IMarkedQuizAnswers {
	const questionMap = new Map(questions.map((q) => [q.id, q]));
	const apiById = resolution
		? new Map(resolution.apiQuestions.map((q) => [q.id, q]))
		: undefined;
	const locale = resolution?.locale ?? ("en" as SupportedLocale);
	const markedAnswers: IMarkedQuizAnswer[] = [];

	for (const ma of apiMarkedAnswers) {
		const question = questionMap.get(ma.questionId);
		if (!question) continue;

		const converted = convertSingle(question, ma, apiById?.get(ma.questionId), locale);
		if (converted) markedAnswers.push(converted);
	}

	return {
		markedAnswers,
		correct: apiResult.correct,
		incorrect: apiResult.incorrect,
		requiredScore: apiResult.requiredScore,
		pass: apiResult.pass
	};
}

function convertSingle(
	question: IQuizQuestion,
	ma: ApiMarkedAnswer,
	apiQ: InlineQuizApiQuestion | undefined,
	locale: SupportedLocale
): IMarkedQuizAnswer | null {
	const isCorrect = ma.correct ?? ma.isCorrect ?? false;
	const base = {
		questionId: question.id,
		title: question.title || "",
		correct: isCorrect,
		questionType: question.type
	};

	const userAnswer = ma.userAnswer;
	const correctAnswer = ma.correctAnswer;

	if (isSingleChoiceQuestion(question) && question.options) {
		const userAnswerDisplay =
			typeof userAnswer === "boolean"
				? question.options[userAnswer ? 0 : 1]
				: (mapCanonicalToDisplayValue(apiQ, userAnswer as string | number, locale) ??
					String(userAnswer ?? ""));
		const correctAnswerDisplay =
			typeof correctAnswer === "boolean"
				? question.options[correctAnswer ? 0 : 1]
				: (mapCanonicalToDisplayValue(apiQ, correctAnswer as string | number, locale) ??
					String(correctAnswer ?? ""));
		const options = question.options.map((value) => ({
			value,
			// Only reveal correct (green) when user got it right; when wrong, only mark user's choice as incorrect
			correct:
				isCorrect && value === correctAnswerDisplay
					? true
					: value === userAnswerDisplay && !isCorrect
						? false
						: undefined
		}));
		return { ...base, options, correctAnswer: correctAnswerDisplay as string, questionType: "singleChoice" };
	}

	if (isMultipleChoiceQuestion(question) && question.options) {
		const correctArrRaw = Array.isArray(correctAnswer) ? correctAnswer : [correctAnswer];
		const userArrRaw = Array.isArray(userAnswer) ? userAnswer : [userAnswer];
		const toDisplay = (v: unknown) =>
			mapCanonicalToDisplayValue(apiQ, v as string | number | boolean | null, locale) ?? String(v);
		const correctArr = correctArrRaw.map((v) => toDisplay(v)) as string[];
		const userArr = userArrRaw.map((v) => toDisplay(v)) as string[];
		const options = question.options.map((value) => {
			const isCorrectOption = correctArr.includes(value as never);
			const isUserSelected = userArr.includes(value as never);
			return {
				value,
				// Only reveal correct (green) when user got it right; when wrong, only mark user's choices as incorrect
				correct:
					isCorrect && isCorrectOption ? true : isUserSelected && !isCorrect ? false : undefined
			};
		});
		return { ...base, options, correctAnswers: correctArr as string[], questionType: "multipleChoice" };
	}

	if (isBooleanChoiceQuestion(question)) {
		// When wrong, don't reveal correct answer (True/False)
		return {
			...base,
			option: userAnswer as boolean,
			correctAnswer: isCorrect ? (correctAnswer as boolean) : undefined,
			questionType: "booleanChoice"
		};
	}

	if (isTextQuestion(question)) {
		return {
			...base,
			answer: String(userAnswer ?? ""),
			questionType: "text"
		};
	}

	if (isDragToOrderQuestion(question) && question.options) {
		const correctArr = Array.isArray(correctAnswer) ? (correctAnswer as string[]) : [String(correctAnswer)];
		const userArr = Array.isArray(userAnswer) ? (userAnswer as string[]) : [String(userAnswer)];
		const options = question.options.map((value) => {
			const inCorrectPosition =
				userArr.indexOf(value) >= 0 &&
				correctArr.indexOf(value) >= 0 &&
				userArr.indexOf(value) === correctArr.indexOf(value);
			const inWrongPosition = userArr.indexOf(value) >= 0 && !inCorrectPosition;
			return {
				value,
				// Only reveal correct order when user got it right
				correct: isCorrect ? inCorrectPosition : inWrongPosition ? false : undefined
			};
		});
		return { ...base, options, correctAnswers: correctArr, questionType: "dragToOrder" };
	}

	if (isMatchingOrderQuestion(question) && question.options && question.answers) {
		const correctArr = Array.isArray(correctAnswer) ? (correctAnswer as string[]) : [String(correctAnswer)];
		const userArr = Array.isArray(userAnswer) ? (userAnswer as string[]) : [String(userAnswer)];
		const options = question.options.map((value, i) => {
			const matchCorrect = userArr[i] === correctArr[i];
			return {
				value,
				// Only reveal correct match when user got it right; when wrong, only show incorrect matches
				correct: isCorrect ? matchCorrect : matchCorrect ? undefined : false
			};
		});
		return { ...base, questions: question.answers, options, correctAnswers: correctArr, questionType: "matchingOrder" };
	}

	if (isFillTheBlankQuestion(question)) {
		const userArr = Array.isArray(userAnswer) ? (userAnswer as string[]) : [String(userAnswer)];
		const correctArr = Array.isArray(correctAnswer) ? (correctAnswer as string[]) : [String(correctAnswer)];
		const wordsToBlank = question.wordsToBlank || [];
		const options = wordsToBlank.map((opt, i) => ({
			value: userArr[i] ?? "",
			correct: userArr[i] === correctArr[i],
			// Only show corrected spelling (green) when user got it right
			correctedSpelling: isCorrect ? correctArr[i] : undefined
		}));
		// When wrong, show user's answers instead of correct ones so we don't reveal
		const answerOptions = isCorrect ? wordsToBlank : wordsToBlank.map((_, i) => userArr[i] ?? "");
		return {
			...base,
			options,
			answerOptions,
			fillTheBlankQuestion: question.title || "",
			correctAnswers: correctArr,
			questionType: "fillTheBlank"
		};
	}

	return null;
}
